import { action, observable } from 'mobx'
import { type IValidationSchema, validationContext } from 'validx'
import {
  addGenericErrorForProperty,
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import type { EffectEditorViewModel } from '@/features/legends/effects/EffectEditorViewModel'
import {
  type EffectDescriptor,
  QuestionEditorViewModelBase,
  type QuestionEditorViewModelOptions,
} from '@/features/legends/questions/QuestionEditorViewModelBase'
import type { EffectQuestion } from '@/features/legends/questions/questions'
import { validateObject } from '@/utils/validx-util'

/**
 * Runs an effect when reached, without asking anything. Nothing can
 * reference it, so it has no ID.
 */
export class EffectQuestionEditorViewModel extends QuestionEditorViewModelBase<EffectQuestion> {
  private readonly _schema: IValidationSchema<EffectQuestionEditorViewModel> = {
    triggerWhen: QuestionEditorViewModelBase.triggerWhenValidators,
    effect: [validateObject('There are invalid effects.')],
  }
  private readonly _effectDescriptors: EffectDescriptor[]

  readonly type = 'Effect'

  /**
   * Runs when the element is reached.
   */
  readonly effect: EffectEditorViewModel

  @observable accessor label = ''

  readonly validation = validationContext(this, this._schema)

  constructor(options: QuestionEditorViewModelOptions) {
    super(options)
    this.effect = this.createEffect()
    this.effect.setExpanded(true)
    this.lifecycle.manage(this.effect)
    this._effectDescriptors = [
      { id: 'effect', title: 'Effect', effect: this.effect },
    ]
    this._validateWhileEditing(this._schema)
  }

  /** @inheritDoc */
  get displayText() {
    return this.label
  }

  /** @inheritDoc */
  get cardTitle() {
    return this.label || 'Effect'
  }

  /** @inheritDoc */
  get effectDescriptors() {
    return this._effectDescriptors
  }

  /** @inheritDoc */
  @action
  hydrate(question: EffectQuestion) {
    this._hydrateShared(question)
    this.effect.hydrate(question.effect)
    this.label = question.label ?? ''
  }

  /** @inheritDoc */
  receiveErrors(descriptor: ErrorDescriptor) {
    addGenericErrorForProperty(
      this,
      descriptor.inner?.mutators,
      'effect',
      'There are invalid effects.',
    )
    receiveErrors(this.effect, descriptor.inner)
    super.receiveErrors(descriptor)
  }

  /** @inheritDoc */
  protected _serialize(): EffectQuestion {
    return {
      type: this.type,
      triggerWhen: this.triggerWhen.toJSON(),
      label: this.label,
      effect: this.effect.toJSON(),
    }
  }
}
